import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import './HomePageCMS.css';

// Inline SVG icon components
const SvgIcon: React.FC<{ size?: number; children: React.ReactNode }> = ({ size = 24, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const IconTruck: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></SvgIcon>
);
const IconDollar: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></SvgIcon>
);
const IconShield: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></SvgIcon>
);
const IconTag: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></SvgIcon>
);
const IconShoppingBag: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></SvgIcon>
);
const IconPackage: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><line x1="16.5" y1="9.4" x2="7.5" y2="4.21" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></SvgIcon>
);
const IconGift: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></SvgIcon>
);
const IconCreditCard: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></SvgIcon>
);
const IconPlus: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></SvgIcon>
);
const IconTrash: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></SvgIcon>
);
const IconMove: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><polyline points="5 9 2 12 5 15" /><polyline points="9 5 12 2 15 5" /><polyline points="15 19 12 22 9 19" /><polyline points="19 9 22 12 19 15" /><line x1="2" y1="12" x2="22" y2="12" /><line x1="12" y1="2" x2="12" y2="22" /></SvgIcon>
);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';


interface Feature {
  _id?: string;
  icon: string;
  title_bn: string;
  title_en: string;
  subtitle_bn: string;
  subtitle_en: string;
}

interface HomepageContent {
  id?: number;
  announcement_text_bn: string;
  announcement_text_en: string;
  is_announcement_active: boolean;
  features: Feature[];
}

const ICON_OPTIONS = [
  { value: 'truck', label: 'Truck', component: <IconTruck /> },
  { value: 'dollar-sign', label: 'Dollar', component: <IconDollar /> },
  { value: 'shield-check', label: 'Shield', component: <IconShield /> },
  { value: 'tag', label: 'Tag', component: <IconTag /> },
  { value: 'shopping-bag', label: 'Bag', component: <IconShoppingBag /> },
  { value: 'package', label: 'Package', component: <IconPackage /> },
  { value: 'gift', label: 'Gift', component: <IconGift /> },
  { value: 'credit-card', label: 'Credit Card', component: <IconCreditCard /> },
];

const HomePageCMS: React.FC = () => {
  const [content, setContent] = useState<HomepageContent>({
    announcement_text_bn: '',
    announcement_text_en: '',
    is_announcement_active: true,
    features: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/admin/cms/homepage/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        // Ensure features have stable IDs for Drag and Drop
        if (data.features) {
          data.features = data.features.map((f: any) => ({
            ...f,
            _id: f._id || Math.random().toString(36).substr(2, 9)
          }));
        }
        setContent(data);
      }
    } catch (error) {
      console.error('Error fetching homepage content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContent(prev => ({ ...prev, [name]: value }));
  };

  const handleToggle = () => {
    setContent(prev => ({ ...prev, is_announcement_active: !prev.is_announcement_active }));
  };

  const handleFeatureChange = (index: number, field: keyof Feature, value: string) => {
    const newFeatures = [...(content.features || [])];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    setContent(prev => ({ ...prev, features: newFeatures }));
  };

  const addFeature = () => {
    setContent(prev => ({
      ...prev,
      features: [
        ...(prev.features || []),
        { 
          _id: Math.random().toString(36).substr(2, 9),
          icon: 'truck', 
          title_bn: '', 
          title_en: '', 
          subtitle_bn: '', 
          subtitle_en: '' 
        }
      ]
    }));
  };

  const removeFeature = (index: number) => {
    setContent(prev => ({
      ...prev,
      features: (prev.features || []).filter((_, i) => i !== index)
    }));
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(content.features || []);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setContent(prev => ({ ...prev, features: items }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/admin/cms/homepage/1/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          announcement_text_bn: content.announcement_text_bn,
          announcement_text_en: content.announcement_text_en,
          is_announcement_active: content.is_announcement_active,
          features: content.features,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setContent(data);
        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text: 'Homepage content updated successfully',
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update homepage content',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="homepage-cms-container">
        <div className="cms-card">
          <div className="card-content">
            <div className="cms-skeleton" style={{ height: 40 }}></div>
            <div className="cms-skeleton" style={{ height: 40 }}></div>
            <div className="cms-skeleton" style={{ height: 30, width: '50%' }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="homepage-cms-container">
      <form onSubmit={handleSubmit}>
        {/* Announcement Bar Section */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left">
              <div className="card-icon icon-amber">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0"/>
                </svg>
              </div>
              <h3>Announcement Bar</h3>
            </div>
            
            <div className="header-toggle">
              <label htmlFor="announcement-toggle">
                {content.is_announcement_active ? 'Active' : 'Inactive'}
              </label>
              <label className="compact-switch">
                <input
                  id="announcement-toggle"
                  type="checkbox"
                  checked={content.is_announcement_active}
                  onChange={handleToggle}
                />
                <span className="compact-slider"></span>
              </label>
            </div>
          </div>
          
          <div className="card-content">
            <div className="cms-grid-2">
              <div className="cms-form-group">
                <label>Announcement Text (Bangla)</label>
                <input
                  type="text"
                  name="announcement_text_bn"
                  value={content.announcement_text_bn}
                  onChange={handleInputChange}
                  placeholder="বিনামূল্যে ডেলিভারি ৳৫০০ এর উপরে অর্ডারে"
                  className="form-input"
                />
              </div>

              <div className="cms-form-group">
                <label>Announcement Text (English)</label>
                <input
                  type="text"
                  name="announcement_text_en"
                  value={content.announcement_text_en}
                  onChange={handleInputChange}
                  placeholder="Free Delivery on orders over ৳500"
                  className="form-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left">
              <div className="card-icon icon-blue">
                <IconGift size={20} />
              </div>
              <h3>Features / Benefits</h3>
            </div>
            <button type="button" className="btn-add-feature" onClick={addFeature}>
              <IconPlus /> Add Feature
            </button>
          </div>
          
          <div className="card-content">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="features-list">
                {(provided) => (
                  <div 
                    className="features-list"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {(content.features || []).map((feature, index) => (
                      <Draggable key={feature._id || index} draggableId={feature._id || `feature-${index}`} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`feature-item-card ${snapshot.isDragging ? 'dragging' : ''}`}
                            style={{ ...provided.draggableProps.style }}
                          >
                            <div className="feature-header">
                              <div className="feature-header-left">
                                <span 
                                  {...provided.dragHandleProps}
                                  className="drag-handle"
                                  title="Drag to reorder"
                                >
                                  <IconMove />
                                </span>
                                <span className="feature-count">#{index + 1}</span>
                              </div>
                              <button type="button" className="btn-remove-feature" onClick={() => removeFeature(index)}>
                                <IconTrash />
                              </button>
                            </div>
                            
                            <div className="feature-grid">
                              <div className="feature-icon-select">
                                <label>Icon</label>
                                <div className="icon-options">
                                  {ICON_OPTIONS.map(option => (
                                    <div 
                                      key={option.value}
                                      className={`icon-option ${feature.icon === option.value ? 'selected' : ''}`}
                                      onClick={() => handleFeatureChange(index, 'icon', option.value)}
                                      title={option.label}
                                    >
                                      {option.component}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className="feature-inputs">
                                <div className="cms-grid-2">
                                  <div className="cms-form-group">
                                    <label>Title (Bangla)</label>
                                    <input
                                      type="text"
                                      value={feature.title_bn}
                                      onChange={(e) => handleFeatureChange(index, 'title_bn', e.target.value)}
                                      className="form-input"
                                      placeholder="Example: দ্রুত ডেলিভারি"
                                    />
                                  </div>
                                  <div className="cms-form-group">
                                    <label>Title (English)</label>
                                    <input
                                      type="text"
                                      value={feature.title_en}
                                      onChange={(e) => handleFeatureChange(index, 'title_en', e.target.value)}
                                      className="form-input"
                                      placeholder="Example: Fast Delivery"
                                    />
                                  </div>
                                  <div className="cms-form-group">
                                    <label>Subtitle (Bangla)</label>
                                    <input
                                      type="text"
                                      value={feature.subtitle_bn}
                                      onChange={(e) => handleFeatureChange(index, 'subtitle_bn', e.target.value)}
                                      className="form-input"
                                      placeholder="Example: ১-২ দিন"
                                    />
                                  </div>
                                  <div className="cms-form-group">
                                    <label>Subtitle (English)</label>
                                    <input
                                      type="text"
                                      value={feature.subtitle_en}
                                      onChange={(e) => handleFeatureChange(index, 'subtitle_en', e.target.value)}
                                      className="form-input"
                                      placeholder="Example: 1-2 Days"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    
                    {(content.features || []).length === 0 && (
                      <div className="empty-state">
                        <p>No features added yet. Click "Add Feature" to start.</p>
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>

        <div className="cms-page-actions">
          <button type="submit" className="btn-save" disabled={saving}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HomePageCMS;
